import { Flex, useWindowSize } from '@solinkcorp/solink-components';
import { useQueryParams } from '@solinkcorp/utils/hooks';
import classNames from 'classnames';
import PlayerCameraSlidePicker from 'components/PlayerCameraSlidePicker/PlayerCameraSlidePicker';
import PublicWrapper from 'components/PublicPages/PublicWrapper';
import { type DownloadOption } from 'components/PublicPages/types/camera.types';
import { type Event, type Info, type ShareCookie } from 'components/PublicPages/types/event.types';
import { PlayingCamerasController } from 'components/VideoPlayer/controller/PlayingCamerasController';
import usePublicPlaybackController from 'components/VideoPlayer/controller/usePublicPlaybackController';
import { VideoPlayerOptionsController } from 'components/VideoPlayer/controller/VideoPlayerOptionsController';
import PublicVideoPlayer from 'components/VideoPlayer/PublicVideoPlayer';
import isMobile from 'ismobilejs';
import Cookie from 'js-cookie';
import React, { useEffect, useMemo, useState } from 'react';
import useDimensions from 'react-cool-dimensions';
import Skeleton from 'react-loading-skeleton';
import { useParams } from 'react-router-dom';
import { useControllerState, useLocalController } from 'solox';
import useLocale from 'src/hooks/useLocale';
import useSWR from 'swr';
import isShallowEqual from 'utils/isShallowEqual';
import { type CamerasDictionary } from 'utils/types';
import { THUMBNAIL_TYPE_PUBLIC_CAMERA } from 'vd-constants';
import { Button } from '../Button';
import { Error } from '../Error';
import { Footer } from '../Footer';
import { Header } from '../Header';
import { SubHeader } from '../SubHeader';
import usePlayingCamsPlaylistStartAndEnd from '../usePlayingCamsPlaylistStartAndEnd';
import CloudAPI from '../utils/api';
import {
  getDownloadOptionsByCameraId,
  getPlayListUrl,
  getUserRestrictions,
} from '../utils/helperFunctions';
import { mightCleanCloudFrontCookie, SOLINK_CLOUD_COOKIE_DOMAIN } from './shareCookieUtils';
import { ShareDetails } from './ShareDetails';

const playerType = 'publicShare';
const isMobileDevice = isMobile(navigator.userAgent).phone;

//  Component
const Share: React.FC = () => {
  /* Set the page title */
  document.title = 'Solink Share';

  const locale = useLocale();

  const [query] = useQueryParams();
  const cameraId = query.get('cameraId') || '';

  const [downloadOptionsByCameraId, setDownloadOptionsByCameraId] = useState<{
    [key: string]: DownloadOption;
  } | null>(null);
  const [pollingInt, setPollingInt] = useState(5000);
  const [camerasById, setCamerasById] = useState<CamerasDictionary>({});
  const [sitesById, setSitesById] = useState({});
  const [devicesById, setDevicesById] = useState<Record<string, any>>({});

  const params = useParams();
  /* Extract the token from the url */
  const token = params.shareId || '';

  const videoPlayerOptionsController = useLocalController(
    () => new VideoPlayerOptionsController(false),
  );

  const playingCamerasController = useLocalController(() => new PlayingCamerasController(false));

  const onSuccessShareEvent = async (result: {
    event: Event;
    cookies: ShareCookie[];
    info: Info;
  }) => {
    const { event, cookies, info } = result;
    /* Sets cookies from the fetched results */
    if (cookies) {
      for (const cookie of mightCleanCloudFrontCookie(cookies, info.tenantId, info.eventId)) {
        Cookie.set(cookie.name, cookie.value, {
          domain: SOLINK_CLOUD_COOKIE_DOMAIN,
          path: `/${info.tenantId}/shares/${info.eventId}/`,
        });
      }
    }
    if (event.pushToCloudStatus !== 'inProgress') {
      setPollingInt(0);
    }

    const userRestrictions =
      event?.shares && info ? getUserRestrictions(event.shares, info) : { downloads: false };

    if (userRestrictions.downloads) {
      setDownloadOptionsByCameraId(await getDownloadOptionsByCameraId(event.cameras, locale));
    }

    setSitesById({ [event.locationId]: {} });
    setDevicesById({ [event.locationId]: {} });
    updateCamerasById(event);

    const targetCamera = event.cameras.find((cam) => cam.id === cameraId);

    const playingCamera = targetCamera ?? event.cameras[0];

    const newPlayingCameraIds = [
      {
        locationId: event.locationId,
        chCameraId: playingCamera.chId,
        vmsCameraId: playingCamera.id,
        playerId: `${playingCamera.id}-${event.locationId}`,
      },
    ];

    playingCamerasController.setPlayingCameras(newPlayingCameraIds);
  };

  const updateCamerasById = (shareEvent: any) => {
    const newCamerasById = shareEvent.cameras.reduce((acc: any, cam: any) => {
      const videoUrl = getPlayListUrl(cam);

      acc[cam.chId] = {
        ...cam,
        location: {},
        audioParams: {},
        streams: cam.streams.map((stream: any) => ({
          ...stream,
          // initialize the earliestSegmentDate to 1
          // This ensures there is a stream available for the video player to play intially
          // Once the playlist is loaded we will update this value
          earliestSegmentDate: 1,
        })),
        videoUrl,
        deviceId: shareEvent.locationId,
      };
      return acc;
    }, {});

    setCamerasById(newCamerasById);
  };

  const {
    data,
    error: swrError,
    isLoading,
  } = useSWR('publicShare', () => CloudAPI.parseToken(token), {
    refreshInterval: pollingInt,
    onSuccess: onSuccessShareEvent,
    errorRetryCount: 2,
    onError: () => setPollingInt(0),
  });

  const event = data?.event;
  const info = data?.info;
  const device = data?.device;
  const error = swrError ? locale.spa.requestedResourceIsUnavailable : undefined;
  const dataReady = !isLoading;

  const filterDate = event?.startTime ? new Date(event.startTime).valueOf() : Date.now().valueOf();

  const cameras = useMemo(
    () =>
      event?.cameras.map((cam: any) => ({
        ...cam,
        id: cam.chId,
        cameraId: cam.id,
        deviceId: event.locationId,
        siteId: event.locationId,
      })) ?? [],
    [event],
  );

  const { playingCameraIds, playingCameras } = useControllerState(
    playingCamerasController.state,
    (state) => ({
      playingCameraIds: state.playingCameraIds,
      playingCameras: playingCamerasController.getPlayingCameras(),
    }),
    isShallowEqual,
  );

  const playbackController = usePublicPlaybackController(
    playingCameras,
    filterDate,
    { vtt: true },
    undefined,
    undefined,
    device?.timezoneId || 'UTC',
  );

  const desiredTime = useControllerState(playbackController.state, (state) => state.desiredTime);

  const { start, end } = usePlayingCamsPlaylistStartAndEnd(playingCameraIds, playbackController);

  const camerasByIdWithSegmentDate = useMemo(() => {
    if (!start) return camerasById;
    return Object.keys(camerasById).reduce((acc: CamerasDictionary, key) => {
      const camera = camerasById[key];
      acc[key] = {
        ...camera,
        streams: camera.streams?.map((stream) => ({
          ...stream,
          earliestSegmentDate: start,
        })),
      };
      return acc;
    }, {});
  }, [camerasById, start]);

  useEffect(() => {
    if (start) playbackController.updateVideoPlaybackStartsAtDate(new Date(start));
    if (end) playbackController.updateVideoPlaybackEndsAtDate(new Date(end));
  }, [start, end, playbackController]);

  useEffect(() => {
    playbackController.seekToTime(filterDate);
  }, [filterDate, playbackController]);

  const windowSize = useWindowSize();

  const { width: playerWidth, height: playerHeight, observe: playerRef } = useDimensions();

  const availableSpace = { width: playerWidth, height: playerHeight };

  // Style variables
  const appClass = classNames('public-app', {
    loading: !dataReady,
    'data-ready': dataReady,
    error: swrError,
  });

  const locationParts = event?.subtitle.split(' @ ');
  const locationName = locationParts?.[1] ?? locationParts?.[0];

  const endTime = React.useMemo(() => new Date(event?.endTime), [event]);

  const startTime = React.useMemo(() => new Date(event?.startTime), [event]);

  const timelineEvent = React.useMemo(() => {
    return {
      ...event,
      startTime,
      endTime,
    };
  }, [event, startTime, endTime]);

  const inProgressEvents = React.useMemo(() => {
    if (
      endTime &&
      startTime &&
      desiredTime > startTime.valueOf() &&
      desiredTime < endTime.valueOf()
    ) {
      return [timelineEvent];
    }
    return [];
  }, [desiredTime, startTime, endTime, timelineEvent]);

  const timelineEvents = React.useMemo(() => [timelineEvent], [timelineEvent]);

  let player = (
    <Skeleton className="skeleton-player" baseColor="#1E1E1E" highlightColor="#1F1F1F" />
  );

  if (dataReady) {
    player = (
      <PublicVideoPlayer
        availableSpace={availableSpace}
        windowSize={windowSize}
        playerType={playerType}
        videoPlayerOptionsController={videoPlayerOptionsController}
        playingCamerasController={playingCamerasController}
        timezone={device?.timezoneId || ''}
        filterDate={filterDate}
        playbackController={playbackController}
        sitesById={sitesById}
        camerasById={camerasByIdWithSegmentDate}
        liveHidden={true}
        isMobile={isMobileDevice}
        hideMiniMap={true}
        timelineEvents={timelineEvents}
        pinnedEvent={timelineEvent}
        inProgressEvents={inProgressEvents}
        devicesById={devicesById}
      />
    );
  }

  if (dataReady && error) {
    player = <div className="player-fake"></div>;
  }

  return (
    <Flex className={appClass} alignItems="center" flexDirection="column">
      <Flex className="container" alignItems="center" flexDirection="column">
        <Header info={info} event={event} sharePage={true} dataReady={dataReady} error={error} />
        <SubHeader
          sharePage={true}
          event={event}
          info={info}
          locationName={locationName}
          device={device}
          error={error}
        />
        <div className="main-content">
          <div className={classNames('player-container', { mobile: isMobileDevice })}>
            {error && <Error>{error}</Error>}
            <div className="player" ref={playerRef}>
              {player}
            </div>{' '}
            <PlayerCameraSlidePicker
              cameras={cameras}
              downloadOptionsByCameraId={downloadOptionsByCameraId}
              playingCamerasController={playingCamerasController}
              cameraThumbnailType={THUMBNAIL_TYPE_PUBLIC_CAMERA}
              multiPlayDisabled={isMobileDevice}
            />
          </div>
          <ShareDetails
            event={event}
            dataReady={dataReady}
            device={device}
            locationName={locationName}
          />
        </div>
        <Footer>
          <Button event={event} info={info} sharePage={true} buttonClass="footer-button" />
        </Footer>
      </Flex>
    </Flex>
  );
};

const ComposedShare = () => {
  return (
    <PublicWrapper>
      <Share />
    </PublicWrapper>
  );
};
export default ComposedShare;
